import React, { useState, useEffect, useMemo } from 'react';
import { Box, Stack } from '@mui/material';
import { useNavigate } from 'react-router';
import TopBar from "../../../../../../component/TopBar";
import DataTable from "../../../../../../component/DataTable";
import MainBox from "../../../../container/MainBox";
import BtnPrimary from "../../../../../../component/PrimaryButton";
import { SearchInput, StatCard, StatusChip, personName } from "../../../../../../component/listing";
import { getAllLeaveType } from "../../../../../../services/leave";
import { getEmployeeNames } from '../../../../../../utils/getEmployeeNames';
import { useNotify } from "../../../../../../component/NotificationProvider";
import { tokens } from "../../../../../../theme";

const isActive = (t) => t?.isActive !== false && t?.status !== "inactive";

const LeaveTypeListing = () => {
  const navigate = useNavigate();
  const notify = useNotify();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [search, setSearch] = useState("");

  const fetchData = async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await getAllLeaveType();
      if (res?.success === false) throw new Error(res?.message || "Failed to load leave types");
      const employeeIds = (res.data || []).filter((d) => d.createdBy).map((d) => d.createdBy);
      const combinedData = await getEmployeeNames(res.data, employeeIds, "createdBy");
      setData(Array.isArray(combinedData) ? combinedData.reverse() : []);
    } catch (err) {
      setError(true);
      notify.error(err?.message || "Failed to load leave types");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return data;
    return data.filter((t) => (t.name || "").toLowerCase().includes(q) || (t.leaveCode || "").toLowerCase().includes(q));
  }, [data, search]);

  const openEdit = (id) => navigate("form", { state: { typeId: id } });

  return (
    <Box sx={{ width: "100%" }}>
      <TopBar MainHeading={"Leave Type"} />

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: "1.5rem" }}>
        <StatCard label="Total leave types" value={data.length} />
        <StatCard label="Active" value={data.filter(isActive).length} accent={tokens.success} />
      </Stack>

      <MainBox sx={{ padding: "1.25rem" }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} justifyContent="space-between" alignItems={{ md: "center" }} sx={{ mb: "1.25rem" }}>
          <SearchInput value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search leave name or code" />
          <BtnPrimary to="form" sx={{ borderRadius: "8px", whiteSpace: "nowrap" }}>
            + Create Leave Type
          </BtnPrimary>
        </Stack>

        <DataTable
          rows={filtered}
          loading={loading}
          error={error}
          onRetry={fetchData}
          errorTitle="Couldn't load leave types"
          empty={{
            title: data.length === 0 ? "No leave types yet" : "No matches",
            description: data.length === 0
              ? "Create leave types (e.g. Casual, Sick) employees can apply for."
              : "Try a different search.",
          }}
          onRowClick={(t) => openEdit(t._id)}
          tableSx={{ width: "100%", tableLayout: "fixed" }}
          columns={[
            { key: "code", label: "Code", render: (t) => t.leaveCode || "—" },
            { key: "name", label: "Leave Name", cellSx: { fontWeight: 600 }, render: (t) => t.name || "—" },
            {
              key: "status",
              label: "Status",
              render: (t) => <StatusChip label={isActive(t) ? "Active" : "Inactive"} tone={isActive(t) ? "success" : "muted"} />,
            },
            { key: "creator", label: "Created By", render: (t) => personName(t?.employee) },
            {
              key: "created",
              label: "Created On",
              cellSx: { color: tokens.textMuted },
              render: (t) => (t.createdAt ? new Date(t.createdAt).toLocaleDateString("en-GB") : "—"),
            },
          ]}
        />
      </MainBox>
    </Box>
  );
};

export default LeaveTypeListing;
